import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from '../../icons';
import { UsageTrend, UsageRanks } from '../../components/shared';
import { getCompanyUsage, type CompanyUsage, type UsageUnit } from '../../api/company-ai';
import { getNumberLocale } from '../../lib/base-i18n';

/**
 * 통합설정 › AI 연결 › **우리 회사 사용량** (판 L).
 *
 * 2026-09-10 사용자 지시: 「회사의 총사용량은 통합설정에서 AI 연결 메뉴에 나왔으면 한다.
 * 그래프로 나와라. 에이전트별, 모델별, 월, 주, 일, 사용자별, 부서별, 회사별로 …」
 *
 * ## ★★★ 이 숫자는 «잰 것의 합»이지 «전부»가 아니다
 *
 * 계량은 provider 가 토큰 수를 알려 줬을 때만 쌓인다(관문 G1 의 규칙 — 모르는 것을 0 으로
 * 적지 않는다). CLI 처럼 안 알려 주는 갈래가 섞이면 실제보다 적게 보인다. 화면이 그 사실을
 * 적어야 관리자가 이 숫자를 청구서로 오해하지 않는다.
 *
 * ## ★★ 부서는 «지금 소속»이다
 *
 * 사람이 팀을 옮기면 **지난 사용량도 새 팀으로 간다.** 사용량 줄에 소속을 박아 두면
 * 「그때 그 부서」와 「지금 그 사람들」이 뒤섞이므로 그렇게 하지 않았다 — 대신 화면이 말한다.
 */

const UNITS: Array<{ key: UsageUnit; label: string }> = [
  { key: 'day', label: '일' },
  { key: 'week', label: '주' },
  { key: 'month', label: '월' },
  { key: 'year', label: '년' },
];

export function CompanyAiUsage(): JSX.Element {
  const [unit, setUnit] = useState<UsageUnit>('day');
  const [data, setData] = useState<CompanyUsage | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (u: UsageUnit) => {
    setBusy(true);
    setError(false);
    try {
      setData(await getCompanyUsage(u));
    } catch {
      setData(null);
      setError(true);
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { void load(unit); }, [load, unit]);

  const fmt = (n: number): string => n.toLocaleString(getNumberLocale());
  const total = data === null ? 0 : data.total.promptTokens + data.total.completionTokens;

  return (
    <section className="company-ai__usage">
      <div className="company-ai__usagehead">
        <h4 className="company-ai__ttl">우리 회사 사용량</h4>
        <div className="company-ai__units" role="group" aria-label="묶는 단위">
          {UNITS.map((u) => (
            <button
              key={u.key}
              type="button"
              className={`company-ai__unit${unit === u.key ? ' is-on' : ''}`}
              aria-pressed={unit === u.key}
              onClick={() => setUnit(u.key)}
            >
              {u.label}
            </button>
          ))}
          <button type="button" className="company-ai__unit" disabled={busy} onClick={() => { void load(unit); }}>
            <RefreshCw size={13} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* ★ 못 읽은 것을 «0» 으로 그리지 않는다 — 「안 썼다」는 거짓이 된다. */}
      {error && (
        <p className="company-ai__note company-ai__note--warn">
          <AlertTriangle size={14} aria-hidden="true" />
          지금 사용량을 읽지 못했습니다. 잠시 후 다시 시도해 주세요.
        </p>
      )}

      {data !== null && (
        <>
          <p className="company-ai__hint">
            {data.from} 부터 · 총 <b>{fmt(total)}</b> 토큰
            (입력 {fmt(data.total.promptTokens)} · 출력 {fmt(data.total.completionTokens)}) ·
            호출 {fmt(data.total.calls)}회
            <br />
            ★ provider 가 토큰 수를 알려 줬을 때만 쌓입니다 — <b>잰 것의 합이지 전부가 아닙니다</b>.
          </p>

          <UsageTrend points={data.series} unit={UNITS.find((u) => u.key === unit)?.label ?? ''} />

          <div className="company-ai__axes">
            <UsageRanks title="에이전트별" rows={data.byAgent} />
            <UsageRanks title="모델별" rows={data.byModel} />
            <UsageRanks title="사용자별" rows={data.byUser} />
            <UsageRanks
              title="부서별"
              rows={data.byOrg}
              note="지금 소속 기준입니다 — 사람이 팀을 옮기면 지난 사용량도 새 팀으로 갑니다."
            />
            <UsageRanks title="연결별" rows={data.byConnection} />
            <UsageRanks title="회사별" rows={data.byInstitution} note="우리 회사 것만 보입니다." />
          </div>
        </>
      )}
    </section>
  );
}
